import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { countryName, searchCountries } from '../countries'
import { useSettle } from './settle'

export interface EditorProps {
  initial: string
  busy: boolean
  onCommit: (v: string) => void
  onCancel: () => void
}

/**
 * 电话输入规范化（M9.1-fix）：
 * - 只保留数字与 `+ - ( ) 空格`，中文/字母一律丢弃（之前完全没拦）
 * - `+` 只允许出现在首位（国际区号）
 * - 数字总个数上限 15（E.164 的最大位数）
 * - 整串长度上限 20（留出分隔符的空间）
 */
function sanitizePhone(raw: string): string {
  const kept = raw.replace(/[^\d+\-() ]/g, '')
  const withPlus = (kept.startsWith('+') ? '+' : '') + kept.replace(/\+/g, '')
  let out = ''
  let digits = 0
  for (const ch of withPlus) {
    if (ch >= '0' && ch <= '9') {
      if (digits >= 15) continue
      digits += 1
    }
    out += ch
    if (out.length >= 20) break
  }
  return out
}

/** 电话列：格内输入，只保留合法字符与 15 位数字上限；原样保存 */
export function PhoneEditor({ initial, busy, onCommit, onCancel }: EditorProps) {
  const [value, setValue] = useState(initial)
  const ref = useRef<HTMLInputElement>(null)
  const s = useSettle(onCommit, onCancel)

  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])

  return (
    <input
      ref={ref}
      className="cell-input"
      inputMode="tel"
      value={value}
      disabled={busy}
      maxLength={20}
      placeholder="+86 138…"
      title="只能输入数字与 + - ( ) 空格，最多 15 位数字"
      onChange={(e) => setValue(sanitizePhone(e.target.value))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') s.commit(value)
        else if (e.key === 'Escape') s.cancel()
      }}
      onBlur={() => s.commit(value)}
    />
  )
}

/** 小时列：原生 time 输入（时分两段），值统一存 "HH:MM" */
export function HourEditor({ initial, busy, onCommit, onCancel }: EditorProps) {
  const [value, setValue] = useState(initial)
  const ref = useRef<HTMLInputElement>(null)
  const s = useSettle(onCommit, onCancel)

  useEffect(() => {
    ref.current?.focus()
  }, [])

  return (
    <input
      ref={ref}
      className="cell-input"
      type="time"
      step={60}
      value={value}
      disabled={busy}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') s.commit(value)
        else if (e.key === 'Escape') s.cancel()
      }}
      onBlur={() => s.commit(value)}
    />
  )
}

/**
 * 国家列：格内输入 + 补全列表。
 *
 * 值存 ISO 3166-1 alpha-2 码（如 CN），显示时用 countryName() 转中文名。
 * 码表来自 board/countries.ts（249 个，取自 monday 逆向常量），中文名由 Intl.DisplayNames 生成。
 */
export function CountryEditor({ initial, busy, onCommit, onCancel }: EditorProps) {
  const initialName = initial === '' ? '' : countryName(initial)
  const [query, setQuery] = useState(initialName)
  const [active, setActive] = useState(0)
  // M9.1-fix：列表改 fixed 定位，否则会被单元格容器的 overflow 裁掉（用户实测看不到候选）
  const [box, setBox] = useState<{ left: number; top: number; width: number } | null>(null)
  const ref = useRef<HTMLInputElement>(null)
  const s = useSettle(onCommit, onCancel)
  // 输入框里还是原位码对应的名字时，列出全部（方便直接挑）
  const options = searchCountries(query === initialName ? '' : query, 30)

  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])

  useLayoutEffect(() => {
    const el = ref.current
    if (el === null) return
    const r = el.getBoundingClientRect()
    setBox({
      left: Math.round(r.left),
      top: Math.round(r.bottom + 2),
      width: Math.round(Math.min(300, Math.max(210, r.width))),
    })
  }, [])

  return (
    <span className="country-cell">
      <input
        ref={ref}
        className="cell-input"
        value={query}
        disabled={busy}
        placeholder="输入国家名或代码"
        onChange={(e) => {
          setQuery(e.target.value)
          setActive(0)
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((a) => Math.min(a + 1, Math.max(0, options.length - 1)))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((a) => Math.max(a - 1, 0))
          } else if (e.key === 'Enter') {
            const code = options[active]
            if (code !== undefined) s.commit(code)
            else s.commit(query)
          } else if (e.key === 'Escape') {
            s.cancel()
          }
        }}
        onBlur={() => s.commit(query)}
      />
      {options.length > 0 && box !== null && (
        <span
          className="country-list"
          role="listbox"
          style={{ position: 'fixed', left: box.left, top: box.top, width: box.width }}
        >
          {options.slice(0, 8).map((code, idx) => (
            <button
              key={code}
              type="button"
              role="option"
              aria-selected={idx === active}
              className={'country-item' + (idx === active ? ' country-item--active' : '')}
              onMouseDown={(e) => {
                e.preventDefault()
                s.commit(code)
              }}
            >
              <span className="country-item__name">{countryName(code)}</span>
              <span className="country-item__code">{code}</span>
            </button>
          ))}
        </span>
      )}
    </span>
  )
}
